import _ from "lodash";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  createContext,
  useContext,
} from "react";
import { StoreApi, UseBoundStore } from "zustand";
import { useShallow } from "zustand/react/shallow";
import {
  IObjectWithTypename,
  NodeStore,
  type ReferenceValueOptionsType,
} from "@/components/@types";
import {
  createNodeFieldsStore,
  ensureObjectsRegistry,
  getFieldDefinitionsFromNode,
  submitNodeToGraphQLServer,
  reconcileServerStateWithPendingOperations,
  buildFieldChangeOperations,
  beginNodeInteraction,
  endNodeInteraction,
} from "@/components/organisms/Node/Node.store";
import { useApolloClient } from "@apollo/client/react";
import { Mutations } from "@/constants";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { useEntity } from "@/hooks/useEntity";
import { useScope } from "@/hooks/useScope";
import { Objects } from "@/objects";
import { ObjectFieldValueType } from "@/objects/@types";
import { idToIdentifier } from "@/objects/@utils";
import { State } from "@/state";
import { useBackendQuery as useQuery } from "@/hooks/useBackendQuery";

const PANEL_ROOT_KEYS = new Set([
  "id",
  "identifier",
  "name",
  "__typename",
  "pendingChange",
]);

/** Scalar / top-level fields the open node panel actually renders. */
function pickPanelFieldValues(
  node: Record<string, unknown> | null | undefined,
  fieldDefinitions: Array<{ objectKey: string; valueKeys?: string[] }>,
  panelDataKeys: string[] = [],
): Record<string, unknown> {
  if (!node) return {};
  const out: Record<string, unknown> = {};
  for (const key of PANEL_ROOT_KEYS) {
    if (key in node) out[key] = node[key];
  }
  const keysToPick = new Set<string>(panelDataKeys);
  for (const fd of fieldDefinitions) {
    keysToPick.add(fd.objectKey.split(".")[0]);
    for (const key of fd.valueKeys ?? []) {
      keysToPick.add(key.split(".")[0]);
    }
  }
  for (const key of keysToPick) {
    if (key in node) out[key] = node[key];
  }
  return out;
}

const getTypename = (options: any) => {
  if (options.mode === "create") return options.typename;
  const nodeTypename = options.node?.__typename;
  if (nodeTypename && Objects[nodeTypename as keyof typeof Objects]) {
    return nodeTypename;
  }
  return options.typename || nodeTypename;
};

const getInitialNode = (options: any, typename: string) =>
  options.mode === "create"
    ? null
    : options?.node && {
        __typename: typename,
        id: btoa(typename + ":{uuid}"),
        identifier: "",
        createdAt: new Date(),
        updatedAt: new Date(),
      };

const buildNode = (options: any, typename: string, initialNode: any) =>
  options.mode === "create"
    ? {
        __typename: typename,
        id: btoa(typename + ":{uuid}"),
        identifier: "",
        createdAt: new Date(),
        updatedAt: new Date(),
      }
    : options.node;

function useNodeMutation(store: UseBoundStore<StoreApi<NodeStore>>) {
  const { mutate, loading } = useBackendMutation({
    mutation:
      store.getState().mode === "create"
        ? Mutations.CREATE_NODE
        : Mutations.UPDATE_NODE,
    callbacks: {
      onError() {
        store.setState({
          fieldValues: {
            ...store.getState().fieldValuesInitial,
          },
          pendingFieldOperations: [],
          interactionLockCount: 0,
        });
      },
    },
  });
  return { mutate, loading };
}

function useNodeSetup(
  options: (
    | { mode: "create"; typename: string }
    | {
        mode: "update" | "get";
        node: IObjectWithTypename;
        typename?: string;
      }
  ) & {
    store?: UseBoundStore<StoreApi<NodeStore>>;
    watchVariables?: any[];
    isPersistedOnExit?: boolean;
    isClearedOnScopedEntityChange?: boolean;
  },
) {
  const typename = getTypename(options);
  const optionsNode = options.mode === "create" ? null : options.node;
  const node = useMemo(
    () => buildNode(options, typename, getInitialNode(options, typename)),
    [options.mode, optionsNode, typename],
  );
  const objectDefinition = Objects[typename];

  // Made once, and holding a record already in hand from its first render:
  // filled only by the effects below, its fields were all drawn empty first
  // and then again - a table's every row, as it was scrolled to.
  const storeRef = useRef<UseBoundStore<StoreApi<NodeStore>>>(null);
  const seededNodeRef = useRef<typeof node>(null);
  if (storeRef.current == null) {
    if (!options.store && options.mode !== "create" && node != null) {
      seededNodeRef.current = node;
    }
    storeRef.current =
      options.store ||
      createNodeFieldsStore(
        options.mode,
        seededNodeRef.current
          ? {
              objectType: objectDefinition,
              typename,
              fieldValues: { __typename: typename, ...node },
            }
          : undefined,
      );
  }
  const store = storeRef.current;
  const isSetup = storeRef.current(
    useShallow(
      (state) => state.objectType != null && state.fieldDefinitions.length > 0,
    ),
  );
  // The scoped business alone, read from the store: ``useScope`` would watch
  // the user and accounts queries in every store - a table's every cell.
  const scopedActiveEntityIdentifier = State(
    (state) =>
      state.scopedEntityIdentifier ?? state.activeEntityIdentifiers?.[0],
  );
  const previousScopedActiveEntityIdentifier = useRef(
    scopedActiveEntityIdentifier,
  );

  const mode = store(useShallow((state) => state.mode));
  const id =
    node?.id || (mode === "create" ? btoa(node.__typename + ":{uuid}") : null);

  const entityIdentifierForCreate = State((state) =>
    store.getState().mode === "create"
      ? state.scopedEntityIdentifier || state.activeEntityIdentifiers?.[0]
      : null,
  );
  const { mutate, loading } = useNodeMutation(store);

  // Clear on scoped entity change
  useEffect(() => {
    if (
      store.getState().mode === "create" &&
      options.isClearedOnScopedEntityChange &&
      scopedActiveEntityIdentifier !==
        previousScopedActiveEntityIdentifier.current
    ) {
      previousScopedActiveEntityIdentifier.current =
        scopedActiveEntityIdentifier;
      void ensureObjectsRegistry().then(() => {
        store.setState({
          fieldValues: {
            ...store.getState().fieldValuesInitial,
          },
          fieldValuesInitial: {
            ...store.getState().fieldValuesInitial,
          },
          fieldDefinitions: getFieldDefinitionsFromNode(store, null, typename),
          objectType: objectDefinition,
        });
      });
    }
  }, [scopedActiveEntityIdentifier, options.isClearedOnScopedEntityChange]);

  // Push ``objectType`` / ``typename`` into the store so its internal
  // subscriber (see ``createNodeFieldsStore``) can derive
  // ``fieldDefinitions`` reactively from ``fieldValues`` going forward.
  // The setup-field-values effect below seeds the store with the snapshot
  // ``node`` data; the subscriber then re-evaluates dynamic
  // ``isVisible`` / ``isEditable`` predicates whenever the user edits a
  // sibling field (e.g. trigger-type → schedule rows hide).
  useLayoutEffect(() => {
    if (
      store.getState().typename === typename &&
      store.getState().objectType === objectDefinition
    ) {
      return;
    }
    store.setState({ objectType: objectDefinition, typename });
  }, [typename, store, objectDefinition]);

  const interactionEpoch = store((state) => state.interactionEpoch);

  // Setup field values
  useLayoutEffect(() => {
    if (isSetup && options.isPersistedOnExit) return;
    if (node != null) {
      if (loading) return;
      // Made holding this very record: there is nothing to set up yet.
      if (node === seededNodeRef.current) {
        seededNodeRef.current = null;
        return;
      }

      const currentState = store.getState();
      const currentNodeId = currentState.fieldValues?.id;
      const newNodeId = node?.id;

      const shouldUpdateFieldValues =
        !currentNodeId ||
        currentNodeId !== newNodeId ||
        Object.keys(currentState.fieldValues).length <= 1;

      if (shouldUpdateFieldValues) {
        // Same record: keep anything already loaded out of band (group data such
        // as document line items, which is fetched separately and can land
        // before this query does). A different record starts clean — its
        // predecessor's group data must not leak across.
        const isSameNode = !currentNodeId || currentNodeId === newNodeId;
        store.setState({
          fieldValues: {
            ...(isSameNode ? currentState.fieldValues : {}),
            __typename: typename,
            ...node,
          } as Record<string, ObjectFieldValueType>,
          fieldValuesInitial: {
            ...(isSameNode ? currentState.fieldValuesInitial : {}),
            __typename: typename,
            ...node,
          } as Record<string, ObjectFieldValueType>,
          pendingFieldOperations: [],
          interactionLockCount: 0,
        });
      } else {
        // Don't clobber live fieldValues while the user is mid-gesture
        // (focus / drag). Unlock bumps interactionEpoch so we re-run.
        if (currentState.interactionLockCount > 0) {
          return;
        }

        const pendingOps = currentState.pendingFieldOperations;
        const fieldDefs = currentState.fieldDefinitions;

        const serverState = {
          __typename: typename,
          ...node,
        } as Record<string, unknown>;

        // Server snapshot is the baseline. Keep applying ops the server has not
        // absorbed yet (especially deletes); drop only acked / safely-submitted ops.
        const {
          fieldValues: updatedFieldValues,
          pendingFieldOperations: nextOps,
        } = reconcileServerStateWithPendingOperations(serverState, pendingOps);

        // The payload only speaks for the fields it asked for. Group data
        // fetched separately — document line items, deliberately kept off the
        // node fragment — is absent from it, and rebuilding it here is
        // destructive either way: replaying ops onto the missing value invents a
        // list containing only the edited rows, and dropping those ops as
        // "submitted" collapses it to a stale snapshot mid-save. Both then get
        // saved, deleting the rows the payload never mentioned. Leave such keys
        // exactly as the store holds them; the query that owns them keeps them
        // current.
        for (const key of Object.keys(updatedFieldValues)) {
          if (!(key in serverState)) {
            delete (updatedFieldValues as Record<string, unknown>)[key];
          }
        }

        // A node refresh only speaks for the fields it asked for. Group data
        // loaded out of band — document line items, say, deliberately kept off
        // the node fragment — is simply absent from the payload, not cleared by
        // the server. Layer the server's answer over what the store holds
        // rather than replacing it: replacing blanked the group on every save,
        // since a save reconciles against a mutation response that carries no
        // line items either.
        const mergedInitial = {
          ...currentState.fieldValuesInitial,
          ...serverState,
        } as Record<string, ObjectFieldValueType>;
        const mergedValues = {
          ...currentState.fieldValues,
          ...updatedFieldValues,
        } as Record<string, ObjectFieldValueType>;

        const panelDataKeys = objectDefinition?.panelDataKeys ?? [];
        const nextInitial = pickPanelFieldValues(
          mergedInitial,
          fieldDefs,
          panelDataKeys,
        ) as Record<string, ObjectFieldValueType>;
        const prevInitial = pickPanelFieldValues(
          currentState.fieldValuesInitial as Record<string, unknown>,
          fieldDefs,
          panelDataKeys,
        );
        const nextPanel = pickPanelFieldValues(
          mergedValues,
          fieldDefs,
          panelDataKeys,
        );
        const prevPanel = pickPanelFieldValues(
          currentState.fieldValues as Record<string, unknown>,
          fieldDefs,
          panelDataKeys,
        );

        // Compared on the merged result, so a payload that simply omits the
        // out-of-band keys settles instead of rewriting state on every pass.
        if (
          fieldDefs.length > 0 &&
          _.isEqual(nextPanel, prevPanel) &&
          _.isEqual(nextInitial, prevInitial) &&
          _.isEqual(nextOps, pendingOps)
        ) {
          return;
        }
        store.setState({
          fieldValuesInitial: mergedInitial,
          pendingFieldOperations: nextOps,
          fieldValues: mergedValues,
        });
      }
    } else {
      const currentNodeId = store.getState().fieldValues?.id;
      if (typeof currentNodeId === "string" && currentNodeId.length > 0) {
        return;
      }
      store.setState({
        fieldValues: { __typename: typename },
        fieldValuesInitial: { __typename: typename },
      });
    }
  }, [
    node,
    entityIdentifierForCreate,
    store,
    isSetup,
    loading,
    typename,
    options.isPersistedOnExit,
    options.watchVariables,
    interactionEpoch,
  ]);

  // Cleanup on unmount - of a store it was handed, which outlives it. One of
  // its own goes with it; emptied, a remount (StrictMode's) filled it again
  // and drew all it holds twice more.
  useLayoutEffect(() => {
    return () => {
      if (options.isPersistedOnExit || !options.store) return;
      store.setState({
        fieldDefinitions: [],
        fieldValues: {},
        fieldValuesInitial: {},
        pendingFieldOperations: [],
        interactionLockCount: 0,
        objectType: null,
        typename: null,
        entityIdentifier: null,
      });
    };
  }, [options.isPersistedOnExit, store]);

  const reset = useCallback(() => {
    store.setState({
      fieldValuesInitial: {
        ...store.getState().fieldValues,
      },
    });
  }, [store]);

  return {
    mutate,
    loading,
    isSetup: store.getState().objectType != null,
    reset,
    store,
  };
}

function submitNodeForStore(
  store: UseBoundStore<StoreApi<NodeStore>>,
  mutate: ReturnType<typeof useNodeMutation>["mutate"],
  apolloClient: ReturnType<typeof useApolloClient>,
) {
  const typename = store.getState().typename;
  const objectType = Objects[typename];
  if (objectType?.submitNode) {
    return objectType.submitNode({ store, apolloClient });
  }
  return submitNodeToGraphQLServer(store, mutate);
}

/**
 * Whether a required field still has nothing in it: no value, blank text or
 * an empty list. A zero or ``false`` is a value.
 */
export function isRequiredFieldMissing(
  field: { isRequired?: boolean; objectKey: string },
  fieldValues: Record<string, unknown>,
): boolean {
  if (!field.isRequired) return false;
  const value = _.get(fieldValues, field.objectKey);
  return (
    value == null ||
    (typeof value === "string" && value.trim() === "") ||
    (Array.isArray(value) && value.length === 0)
  );
}

function useNode<T extends IObjectWithTypename>(
  store: UseBoundStore<StoreApi<NodeStore>>,
) {
  const apolloClient = useApolloClient();
  const { mutate, loading } = useNodeMutation(store);
  const node = store(useShallow((state) => state.fieldValues as T));
  const typename = store(useShallow((state) => state.typename));
  const objectType = store(useShallow((state) => state.objectType));
  const fieldDefinitions = store((state) => state.fieldDefinitions);
  // ``objectKey`` may be a dotted path (e.g. ``"profile.description"``) for
  // nested API shapes; ``_.get`` resolves both flat and nested.
  const missingRequiredKeys = store(
    useShallow((state) =>
      state.fieldDefinitions
        .filter((field) => isRequiredFieldMissing(field, state.fieldValues))
        .map((field) => field.objectKey),
    ),
  );
  const isDisabled = missingRequiredKeys.length > 0;
  // What submit still needs, as it reads to the user: "a name and a contact".
  const missingRequiredLabels = fieldDefinitions
    .filter((field) => missingRequiredKeys.includes(field.objectKey))
    .map((field) => {
      const name =
        field.objectKey === objectType?.titleFieldKey
          ? "title"
          : (field.label || field.objectKey).toLowerCase();
      return `${/^[aeiou]/.test(name) ? "an" : "a"} ${name}`;
    });
  const isDirty = store(
    useShallow(
      (state) => !_.isEqual(state.fieldValues, state.fieldValuesInitial),
    ),
  );
  return {
    isDirty,
    isDisabled,
    missingRequiredLabels,
    fieldDefinitions,
    fieldValues: node,
    actions: objectType?.getActions?.(node) || [],
    events: {
      onSubmitNode: () => submitNodeForStore(store, mutate, apolloClient),
      /** Outline the required fields still empty, and put the cursor in the first. */
      onRevealMissingRequired: () => {
        store.setState((state) => ({
          missingRequiredRevealCount: state.missingRequiredRevealCount + 1,
        }));
        requestAnimationFrame(() => {
          const first = document.querySelector<HTMLElement>(
            '[data-missing-required="true"]',
          );
          (
            first?.querySelector<HTMLElement>(
              "input, textarea, [contenteditable='true']",
            ) ?? first
          )?.focus();
        });
      },
      onChangeNode: (key, value) =>
        store.setState({
          fieldValues: {
            ...store.getState().fieldValues,
            [key]: value,
          },
        }),
      onDeleteNode: () => null,
    },
    metadata: {
      entityId: objectType?.getEntityId?.(node) || null,
      humanName: objectType?.humanName || "",
      isEntity: objectType?.isEntity || false,
      mode: store.getState().mode,
      objectKey: objectType?.inputKey || typename,
      title: objectType?.getTitle(node) || "",
      typename: typename,
      ReactIcon: objectType?.ReactIcon,
    },
    isSubmitting: loading,
    mutate: mutate,
  };
}

function useNodeField(
  store: UseBoundStore<StoreApi<NodeStore>>,
  fieldObjectKey: string,
) {
  const apolloClient = useApolloClient();
  const mode = store.getState().mode;
  const { mutate: createOrUpdateNode } = useNodeMutation(store);
  const mutate = mode !== "get" ? createOrUpdateNode : undefined;
  const fieldDefinition = store(
    useShallow((state) =>
      state.fieldDefinitions.find(
        (fieldDefinition) => fieldDefinition.objectKey === fieldObjectKey,
      ),
    ),
  );

  // ``valueKeys`` may include dotted paths (e.g. ``"profile.description"``)
  // when the underlying object is nested. The view must reconstruct that
  // nesting so a field's ``getValue: (d) => d?.profile?.description``
  // resolves correctly. We use ``_.set``/``_.get`` to round-trip dotted
  // paths without forcing every Object definition to mirror its API
  // shape with flat keys.
  const fieldValues = store(
    useShallow((state) =>
      (fieldDefinition?.valueKeys || [fieldObjectKey]).reduce((acc, key) => {
        return _.set(acc, key, _.get(state.fieldValues, key));
      }, {}),
    ),
  );
  const onInteractionStart = useCallback(() => {
    beginNodeInteraction(store);
  }, [store]);
  const onInteractionEnd = useCallback(() => {
    endNodeInteraction(store);
  }, [store]);

  const fieldEvents = useMemo(() => {
    return (
      fieldDefinition?.isEditable && {
        onChangeField: (value) => {
          const state = store.getState();
          const next = _.cloneDeep(state.fieldValues);
          const previousValue = _.get(state.fieldValues, fieldObjectKey);
          _.set(next, fieldObjectKey, value);
          const newOps = buildFieldChangeOperations(
            fieldObjectKey,
            fieldDefinition?.type,
            previousValue,
            value,
          );
          store.setState({
            fieldValues: next,
            pendingFieldOperations: [
              ...state.pendingFieldOperations,
              ...newOps,
            ],
          });
        },
        onSubmitField:
          mode === "update"
            ? () => submitNodeForStore(store, mutate, apolloClient)
            : () => null,
        onDeleteField: (index?: number) => {
          if (!index) {
            const next = _.cloneDeep(store.getState().fieldValues);
            _.set(next, fieldObjectKey, null);
            store.setState({ fieldValues: next });
          } else {
            throw new Error("onDelete not implemented for index");
          }
        },
        onInteractionStart,
        onInteractionEnd,
      }
    );
  }, [
    fieldDefinition?.isEditable,
    fieldDefinition?.type,
    fieldObjectKey,
    store,
    mutate,
    apolloClient,
    mode,
    onInteractionStart,
    onInteractionEnd,
  ]);

  const fieldValuesInitial = store(
    useShallow((state) =>
      (fieldDefinition?.valueKeys || [fieldObjectKey]).reduce((acc, key) => {
        return _.set(acc, key, _.get(state.fieldValuesInitial, key));
      }, {}),
    ),
  );

  return {
    fieldDefinition,
    fieldEvents,
    fieldValues,
    fieldValuesInitial,
  };
}

function useNodeEntity(store: UseBoundStore<StoreApi<NodeStore>>) {
  // The record's business alone: read through ``useNode``, whatever asked for
  // it was drawn again on every keystroke in the record.
  const entityId = store(
    (state) =>
      state.objectType?.getEntityId?.(state.fieldValues as never) || null,
  );
  const { scopedActiveEntityIdentifier } = useScope();
  const { entity } = useEntity(
    idToIdentifier(entityId) || scopedActiveEntityIdentifier,
  );
  return {
    entity,
  };
}

export { useNode, useNodeEntity, useNodeField, useNodeSetup };

/**
 * Default ``prepareOptions`` mirrors the convention every reference query
 * follows: the top-level data envelope wraps a connection-shaped payload
 * under its first key (e.g. ``{ documents: { data: [...] } }``). Callers
 * with bespoke shapes pass their own ``prepareOptions``.
 */
const defaultPrepareOptions = (data: unknown): unknown[] => {
  const obj = (data as Record<string, unknown> | null | undefined) ?? null;
  const key = obj ? Object.keys(obj)[0] : undefined;
  if (!key) return [];
  return (
    ((obj?.[key] as { data?: unknown[] } | undefined)?.data as unknown[]) ?? []
  );
};

/**
 * Extracted GraphQL-fetching half of ``<NodeList>``: runs the reference
 * query and returns its prepared options array plus a stable
 * ``isLoading`` flag.
 *
 * Use this hook when you want to compose live reference rows with other
 * data (e.g. upstream-step values in the workflow filter picker) inside
 * a single ``<List>``. ``<NodeList>`` itself is a thin convenience
 * wrapper around this hook that pairs it with a default ``<List>``
 * setup; reach for the hook directly when you need a heterogeneous
 * ``data`` array so that keyboard navigation, search, and click
 * handling all live in one place.
 */
export function useNodeListQuery<T>({
  query,
  queryOptions,
  prepareOptions,
}: ReferenceValueOptionsType<T>): {
  options: T[];
  isLoading: boolean;
} {
  const { data, loading } = useQuery(query, {
    ...queryOptions,
    fetchPolicy: "cache-and-network",
    variables: {
      ...queryOptions?.variables,
    },
  });
  const options = useMemo(() => {
    const prep = (prepareOptions ?? defaultPrepareOptions) as (
      d: unknown,
    ) => T[];
    return prep(data);
  }, [data, prepareOptions]);
  // ``loading && data == null`` matches the behaviour ``<NodeList>``
  // ships today: don't flash the loading state while a cached payload
  // is still on screen during a background refetch.
  const isLoading = loading && data == null;
  return { options, isLoading };
}

// Preserve the context object across Vite HMR of this module, as
// ``List.hooks.tsx`` does. This module imports ``@/objects``, so editing any
// object definition re-runs it; a fresh context left the mounted
// ``NodeLayout`` providing the old one while re-rendered group tabs read the
// new one → ``useNodeLayoutStore must be used within NodeLayout``.
const NodeLayoutStoreContext: React.Context<UseBoundStore<
  StoreApi<NodeStore>
> | null> =
  (
    import.meta.hot?.data as {
      NodeLayoutStoreContext?: React.Context<UseBoundStore<
        StoreApi<NodeStore>
      > | null>;
    }
  )?.NodeLayoutStoreContext ??
  createContext<UseBoundStore<StoreApi<NodeStore>> | null>(null);

if (import.meta.hot) {
  import.meta.hot.dispose(
    (data: { NodeLayoutStoreContext?: typeof NodeLayoutStoreContext }) => {
      data.NodeLayoutStoreContext = NodeLayoutStoreContext;
    },
  );
}

export function NodeLayoutStoreProvider({
  store,
  children,
}: {
  store: UseBoundStore<StoreApi<NodeStore>>;
  children: React.ReactNode;
}) {
  return (
    <NodeLayoutStoreContext.Provider value={store}>
      {children}
    </NodeLayoutStoreContext.Provider>
  );
}

export function useNodeLayoutStore() {
  const store = useContext(NodeLayoutStoreContext);
  if (!store) {
    throw new Error("useNodeLayoutStore must be used within NodeLayout");
  }
  return store;
}

const DEFAULT_CARD_MIN_WIDTH = 400;
const CARD_GAP_UNITS = 2;
const PADDING_UNITS: Record<"sm" | "md", number> = { sm: 1, md: 2 };

export function extractCardMinWidth(card?: { minWidth?: number }): number {
  return card?.minWidth || DEFAULT_CARD_MIN_WIDTH;
}

/**
 * Determines whether to use "tabs" or "cards" layout by measuring whether the
 * cards layout would fit in the available container width. Returns a probeRef
 * to attach to an invisible sizing element rendered in the parent.
 */
export function useResponsiveLayoutMode({
  groups,
  size,
  forceLayoutMode,
  preferredLayoutMode,
}: {
  groups: CardLayoutGroup[];
  size: "sm" | "md";
  forceLayoutMode?: "tabs" | "cards";
  /**
   * The user's choice, honoured only when it is possible: "cards" still
   * defers to the width probe, so a narrow container falls back to tabs.
   * ``forceLayoutMode`` outranks it and ignores width entirely.
   */
  preferredLayoutMode?: "tabs" | "cards";
}): {
  layoutMode: "tabs" | "cards";
  probeRef: React.RefObject<HTMLDivElement>;
  intendedCardsWidth: number;
} {
  const [canFitCards, setCanFitCards] = useState<boolean | null>(null);
  const probeRef = useRef<HTMLDivElement>(null);

  const intendedCardsWidth = useMemo(() => {
    const items = partitionCardLayoutGroups(groups);
    const totalMinWidth = items.reduce(
      (sum, item) => sum + cardLayoutItemMinWidth(item),
      0,
    );
    const gapPixels = CARD_GAP_UNITS * 8;
    const totalGap = Math.max(0, items.length - 1) * gapPixels;
    const paddingPixels = PADDING_UNITS[size] * 8 * 2;
    return totalMinWidth + totalGap + paddingPixels;
  }, [groups, size]);

  useLayoutEffect(() => {
    if (groups.length <= 1) {
      setCanFitCards(false);
      return;
    }
    const probe = probeRef.current;
    if (!probe) return;

    const checkFit = () => {
      const actualWidth = probe.getBoundingClientRect().width;
      setCanFitCards(actualWidth >= intendedCardsWidth - 1);
    };

    checkFit();
    const observer = new ResizeObserver(checkFit);
    observer.observe(probe);
    return () => observer.disconnect();
  }, [intendedCardsWidth, groups.length]);

  const layoutMode: "tabs" | "cards" =
    groups.length <= 1
      ? "tabs"
      : forceLayoutMode
        ? forceLayoutMode
        : preferredLayoutMode !== "tabs" && canFitCards === true
          ? "cards"
          : "tabs";

  return { layoutMode, probeRef, intendedCardsWidth };
}

/** Default percentage layout for resizable wide-panel columns. */
export function buildCardsDefaultLayout(
  groups: { id: string; card?: { flex?: number } }[],
): Record<string, number> {
  const weights = groups.map((group) => {
    return group.card?.flex === 1 ? 2 : 1;
  });
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  const layout: Record<string, number> = {};
  groups.forEach((group, index) => {
    layout[group.id] = (weights[index] / total) * 100;
  });
  return layout;
}

export type CardLayoutGroup = {
  id: string;
  card?: {
    flex?: number;
    minWidth?: number;
    stack?: string;
    stackMode?: "split" | "tabs";
    stackFlex?: number;
    stackAutoSize?: boolean;
  };
};

export type CardLayoutItem =
  | { type: "group"; group: CardLayoutGroup }
  | {
      type: "stack";
      stackId: string;
      /**
       * "split" nests the groups vertically in the column; "tabs" gives the
       * column its own tab bar and shows one group at a time.
       */
      mode: "split" | "tabs";
      groups: CardLayoutGroup[];
    };

/** Partition flat groups into horizontal panels and vertical stacks. */
export function partitionCardLayoutGroups(
  groups: CardLayoutGroup[],
): CardLayoutItem[] {
  const stackMap = new Map<string, CardLayoutGroup[]>();
  for (const group of groups) {
    const stackId = group.card?.stack;
    if (!stackId) continue;
    const stackGroups = stackMap.get(stackId) ?? [];
    stackGroups.push(group);
    stackMap.set(stackId, stackGroups);
  }

  const emittedStacks = new Set<string>();
  const items: CardLayoutItem[] = [];

  for (const group of groups) {
    const stackId = group.card?.stack;
    if (stackId) {
      if (emittedStacks.has(stackId)) continue;
      emittedStacks.add(stackId);
      const stackGroups = stackMap.get(stackId) ?? [group];
      items.push({
        type: "stack",
        stackId,
        mode:
          stackGroups.find((stackGroup) => stackGroup.card?.stackMode)?.card
            ?.stackMode ?? "split",
        groups: stackGroups,
      });
      continue;
    }
    items.push({ type: "group", group });
  }

  return items;
}

export function cardLayoutItemMinWidth(item: CardLayoutItem): number {
  if (item.type === "group") {
    return extractCardMinWidth(item.group.card);
  }
  const stackMinWidth = item.groups.reduce(
    (max, group) => Math.max(max, extractCardMinWidth(group.card)),
    0,
  );
  return Math.max(stackMinWidth, DEFAULT_CARD_MIN_WIDTH);
}

export function buildCardsDefaultLayoutFromItems(
  items: CardLayoutItem[],
): Record<string, number> {
  const weights = items.map((item) => {
    if (item.type === "stack") return 2;
    return item.group.card?.flex === 1 ? 2 : 1;
  });
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  const layout: Record<string, number> = {};
  items.forEach((item, index) => {
    const id = item.type === "stack" ? `stack:${item.stackId}` : item.group.id;
    layout[id] = (weights[index] / total) * 100;
  });
  return layout;
}

export function buildStackDefaultLayout(
  groups: { id: string; card?: { stackFlex?: number } }[],
): Record<string, number> {
  const weights = groups.map((group) => group.card?.stackFlex ?? 1);
  const total = weights.reduce((sum, weight) => sum + weight, 0);
  const layout: Record<string, number> = {};
  groups.forEach((group, index) => {
    layout[group.id] = (weights[index] / total) * 100;
  });
  return layout;
}
